import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@clay-run/glaze/alert';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import { Heading } from '@clay-run/glaze/heading';
import { Text } from '@clay-run/glaze/text';
import { useAppBasePath } from '../../_shared/base-path';
import {
  useLinkedInPost,
  useLinkedInStatus,
  useMetaAudiences,
  useMetaStatus,
  usePublishRequestById,
  useSubmitAdRequest,
  useSubmitLinkedInRequest,
} from '../api-client';
import {
  missingFields,
  missingLinkedInFields,
  newCreative,
  resolvedExclusions,
  toLinkedInSubmit,
  withImportedDestination,
  withNaming,
  type DraftCreative,
} from '../draft';
import { publishedNotice, type RequestPageState } from '../publish-notice';
import { EMPTY_LINKEDIN_DRAFT, EMPTY_META_DRAFT, type MetaDraft } from '../draft-fixtures';
import type { AgentTurnResponse, MetaAdImport, MetaAudienceRef } from '../types';
import AdDraftPanel, { IMAGE_INPUT_ID, type PlatformTab } from './AdDraftPanel';
import AgentInterview from './AgentInterview';
import { ImportAdBar } from './ImportAdBar';

function filledFields(draft: MetaDraft, creatives: DraftCreative[]) {
  const checks = [
    draft.name.trim(),
    draft.primaryText.trim(),
    draft.headline.trim(),
    draft.destination === 'lead_form' ? draft.leadFormId : draft.destinationUrl.startsWith('https://'),
    Number(draft.dailyBudgetUsd) >= 1,
    draft.startDate,
    creatives.every(c => c.media),
  ];
  return { done: checks.filter(Boolean).length, total: checks.length };
}

function applyAgentPatch(draft: MetaDraft, patch: NonNullable<AgentTurnResponse['draftPatch']>): MetaDraft {
  const p = patch;
  return withNaming({
    ...draft,
    ...(p.name !== undefined && { name: p.name }),
    ...(p.team !== undefined && { team: p.team }),
    ...(p.program !== undefined && { program: p.program }),
    ...(p.personas !== undefined && { personas: p.personas }),
    ...(p.primaryText !== undefined && { primaryText: p.primaryText }),
    ...(p.headline !== undefined && { headline: p.headline }),
    ...(p.description !== undefined && { description: p.description }),
    ...(p.callToAction !== undefined && { callToAction: p.callToAction }),
    ...(p.destinationUrl !== undefined && { destinationUrl: p.destinationUrl }),
    ...(p.audiences !== undefined && { targets: p.audiences, targetsEdited: true }),
    ...(p.formDescription !== undefined &&
      draft.leadForm && { leadForm: { ...draft.leadForm, spec: { ...draft.leadForm.spec, questionHeadline: p.formDescription } } }),
    ...(p.dailyBudgetUsd !== undefined && { dailyBudgetUsd: String(p.dailyBudgetUsd) }),
    ...(p.startDate !== undefined && { startDate: p.startDate }),
    ...(p.endDate !== undefined && { endDate: p.endDate ?? '' }),
  });
}

const uniqueById = (audiences: MetaAudienceRef[]) => [...new Map(audiences.map(a => [a.id, a])).values()];

export default function NewRequestPage() {
  const base = useAppBasePath();
  const navigate = useNavigate();
  const [metaDraft, setMetaDraft] = useState(EMPTY_META_DRAFT);
  const [creatives, setCreatives] = useState<DraftCreative[]>(() => [newCreative([])]);
  const status = useMetaStatus();
  const audiences = useMetaAudiences(status.data?.account != null);
  const submit = useSubmitAdRequest();
  const [videoProgress, setVideoProgress] = useState<number | null>(null);
  const [platform, setPlatform] = useState<PlatformTab>('meta');
  const [linkedInDraft, setLinkedInDraft] = useState(EMPTY_LINKEDIN_DRAFT);
  const [linkedInPostUrl, setLinkedInPostUrl] = useState('');
  const linkedInStatus = useLinkedInStatus();
  const linkedInPost = useLinkedInPost(linkedInPostUrl);
  const submitLinkedIn = useSubmitLinkedInRequest();
  const publish = usePublishRequestById();
  const canPublish = status.data?.canPublish ?? false;

  const metaConnected = status.data?.account != null;
  const missing = platform === 'linkedin' ? missingLinkedInFields(linkedInDraft) : missingFields(metaDraft, creatives);
  const pending = submit.isPending || submitLinkedIn.isPending || publish.isPending;
  const submitError = platform === 'linkedin' ? submitLinkedIn.error : submit.error;

  const open = (id: string, state?: RequestPageState) => navigate(`${base}/requests/${id}`, { state });

  // A publisher's own request skips the queue: it's created, then published right away. When that
  // publish is refused (a video still processing), the request page explains and offers Publish.
  const publishCreated = (id: string) =>
    publish.mutate(id, {
      onSuccess: () => open(id),
      onError: err => open(id, { notice: publishedNotice(err) }),
    });

  function create({ andPublish }: { andPublish: boolean }) {
    if (missing.length > 0) return;
    const onCreated = (id: string) => (andPublish ? publishCreated(id) : open(id));
    if (platform === 'linkedin') {
      submitLinkedIn.mutate(toLinkedInSubmit(linkedInDraft, linkedInPost.data?.post?.urn ?? null), { onSuccess: onCreated });
      return;
    }
    setVideoProgress(creatives.some(c => c.media?.video?.file) ? 0 : null);
    submit.mutate(
      { draft: metaDraft, creatives, audiences: audiences.data ?? [], onVideoProgress: setVideoProgress },
      { onSuccess: onCreated, onSettled: () => setVideoProgress(null) },
    );
  }

  const applyImport = (imported: MetaAdImport, importedCreatives: DraftCreative[]) => {
    const d = imported.draft;
    setPlatform('meta');
    setMetaDraft(prev => ({
      ...withImportedDestination(prev, d),
      name: prev.name || imported.sourceName,
      primaryText: d.primaryText || prev.primaryText,
      headline: d.headline || prev.headline,
      description: d.description || prev.description,
      callToAction: d.callToAction ?? prev.callToAction,
      ...(d.targeting.included.length > 0 && { targets: d.targeting.included, targetsEdited: true }),
      // The imported ad's exclusions are added to Growth's defaults, never in place of them.
      ...(d.targeting.excluded.length > 0 && {
        exclusions: uniqueById([...resolvedExclusions(prev, audiences.data ?? []), ...d.targeting.excluded]),
      }),
      dailyBudgetUsd: d.dailyBudgetUsd === null ? prev.dailyBudgetUsd : String(d.dailyBudgetUsd),
    }));
    if (importedCreatives.length > 0) setCreatives(importedCreatives);
  };

  return (
    <section className="flex h-[calc(100vh-180px)] min-h-[640px] flex-col gap-5">
      <ImportAdBar onImport={applyImport} />
      <header className="flex flex-wrap items-center gap-3">
        <Heading level={1} size="lg">
          {(platform === 'linkedin' ? linkedInDraft.name : metaDraft.name) || 'Untitled request'}
        </Heading>
        <Badge color="secondary">Draft</Badge>
        {status.data?.isSandbox && <Badge color="yellow">Meta sandbox</Badge>}
        <div className="ml-auto flex items-center gap-3">
          {missing.length > 0 && (
            <Text size="sm" variant="tertiary">
              Needs {missing.join(', ')}
            </Text>
          )}
          {videoProgress !== null && (
            <Text size="sm" variant="tertiary">
              Uploading video to Meta… {Math.round(videoProgress * 100)}%
            </Text>
          )}
          <Button
            variant={canPublish ? 'secondary' : 'primary'}
            onClick={() => create({ andPublish: false })}
            disabled={missing.length > 0 || pending}
            loading={pending && !publish.isPending}
          >
            Submit for review
          </Button>
          {canPublish && (
            <Button
              variant="primary"
              onClick={() => create({ andPublish: true })}
              disabled={missing.length > 0 || pending}
              loading={publish.isPending}
            >
              {platform === 'linkedin' ? 'Create and publish as a LinkedIn draft' : 'Create and publish paused'}
            </Button>
          )}
        </div>
      </header>

      {submitError && (
        <Alert status="error" error={submitError}>
          {submitError.message}
        </Alert>
      )}
      {platform === 'meta' && status.data && !metaConnected && (
        <Alert status="warning">Meta isn't connected: {status.data.problem ?? 'unknown problem'}</Alert>
      )}

      {platform === 'linkedin' && linkedInStatus.data && !linkedInStatus.data.account && (
        <Alert status="warning">LinkedIn isn't connected: {linkedInStatus.data.problem ?? 'unknown problem'}</Alert>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-[420px_minmax(0,1fr)]">
        <AgentInterview
          draft={metaDraft}
          creativeNames={creatives.map(c => c.name)}
          onDraftPatch={patch => {
            setMetaDraft(d => applyAgentPatch(d, patch));
            const names = patch.creativeNames;
            if (names) setCreatives(cs => cs.map((c, i) => ({ ...c, name: names[i] ?? c.name })));
          }}
          filled={filledFields(metaDraft, creatives)}
          imageInputId={IMAGE_INPUT_ID}
        />
        <AdDraftPanel
          metaDraft={metaDraft}
          onMetaDraftChange={setMetaDraft}
          creatives={creatives}
          onCreativesChange={setCreatives}
          metaConnected={metaConnected}
          platform={platform}
          onPlatformChange={setPlatform}
          linkedIn={{
            draft: linkedInDraft,
            onChange: setLinkedInDraft,
            postUrl: linkedInPostUrl,
            onPostUrlCommit: setLinkedInPostUrl,
            connected: linkedInStatus.data?.account != null,
          }}
        />
      </div>
    </section>
  );
}
